/**
 * Which balance banners the app shell draws, from the wallet's state — the one decision behind
 * `components/BalanceBanners.vue` (`docs/plans/billing-ui.md` § Wave 2, items 1.1, 1.2, 7.3, 7.4
 * and U6, and § Wave 3's delivery half of 5.1).
 *
 * Pure, so a spec pins every state a board draws and the component only renders what this
 * answers. The order of the list is the order on screen: what concerns the balance first, then
 * what concerns the automatic top-up.
 *
 * **The thresholds are the server's, never a figure chosen here.** The runway is the wallet
 * read's own `runway_days` — the low-balance e-mails average the same two windows — and the
 * low and critical banners are silent while the automatic top-up is on, as those e-mails are:
 * the wallet tops itself up before either matters.
 *
 * **Credits on their way change what a banner offers, never what it states.** While a top-up's
 * direct debit is being collected (`incoming_cents`), the low banner is not drawn — the remedy is
 * already paid for — and the critical, empty and deletion banners keep their facts, since the
 * credits are not in the wallet and the public side is offline at zero all the same, but say the
 * credits are coming and offer no second top-up: one would charge the buyer twice.
 */

import type { PublicSideOfflineReason } from '@/api/session/access'
import { deliveryBilledCents, deliveryLevel, type DeliveryState } from './delivery'
import { CATALOG_PRICES, type PriceBook } from './prices'
import { isRunwayShort } from './status'

/** The runway, in days, under which the warning is no longer one that can wait. */
export const CRITICAL_RUNWAY_DAYS = 3

/**
 * How many refusals in a row switch the automatic top-up off — the backend's own figure (D3,
 * `ledger/effects.rs`'s `count_auto_top_up_failure`), which `AutoTopUpView.consecutive_failures`
 * counts toward.
 */
export const AUTO_TOP_UP_FAILURE_LIMIT = 5

/** Ledger's word for a refusal that only asked the customer to confirm with their bank. */
export const AUTHENTICATION_REQUIRED = 'authentication_required'

/**
 * - `low` — the runway is fourteen days or less. Dismissible for the session.
 * - `critical` — three days or less.
 * - `empty` — the balance is at or below zero: the public side is withheld (D14).
 * - `dailyAllowance` — the balance is not empty, but the wallet has never paid and its public
 *   delivery today reached the daily allowance (`docs/plans/anti-abuse-rust.md` decision 10): the
 *   public side is withheld until the day turns, and a first top-up lifts it. Not dismissible. It
 *   carries the platform's figure, `GET /access`'s, which the server publishes beside the reason.
 * - `deletion` — empty, and the Terms of Sale notice has named the first day content may be
 *   deleted. It replaces `empty` rather than stacking a second red line over it.
 * - `confirmation` — an automatic top-up waits for the customer's bank (3-D Secure).
 * - `autoTopUpOff` — the automatic top-up was switched off by its fifth refusal.
 * - `member` — the neutral version, for a member who may not read the wallet: the public side is
 *   withheld, and somebody else holds the remedy (U6). It carries the reason, so the day's
 *   allowance is said to reopen tomorrow rather than to wait on a top-up.
 * - `deliveryNear` — 80 % of the month's free delivery is used (5.1). Dismissible.
 * - `deliveryExceeded` — the free delivery is used up and every gigabyte past it is billed.
 *   Dismissible, and it replaces `deliveryNear`. The component keys both dismissals to the
 *   period, so each shows once a month. Neither is drawn where delivery past the allowance is
 *   offered — priced at zero —, since going past it costs nothing: the worker mails neither then.
 */
export type BalanceBannerKind =
  | 'low'
  | 'critical'
  | 'empty'
  | 'dailyAllowance'
  | 'deletion'
  | 'confirmation'
  | 'autoTopUpOff'
  | 'member'
  | 'deliveryNear'
  | 'deliveryExceeded'

/**
 * `incomingCents`, on the three banners that state a shortage: the credits a direct debit being
 * collected will bring, present only while there are some — the banner then says so and offers no
 * top-up.
 */
export type BalanceBanner =
  | { kind: 'low'; runwayDays: number; spentPerDayCents: number }
  | { kind: 'critical'; runwayDays: number; incomingCents?: number }
  | { kind: 'empty'; incomingCents?: number }
  | { kind: 'dailyAllowance'; allowanceBytes: number | null }
  | { kind: 'deletion'; deletableOn: string; incomingCents?: number }
  | { kind: 'confirmation'; purchaseId: string; amountExclTaxCents: number }
  | { kind: 'autoTopUpOff'; failures: number }
  | { kind: 'member'; reason: PublicSideOfflineReason }
  | { kind: 'deliveryNear'; usedBytes: number; includedBytes: number }
  | {
      kind: 'deliveryExceeded'
      includedBytes: number
      billableBytes: number
      billedCents: number
    }


/** What the wallet read says, of what a banner needs. */
export interface WalletState {
  balance_cents: number
  runway_days?: number | null
  spent_per_day_cents: number
  content_deletable_on?: string | null
  /** The credits of the top-ups whose direct debit is being collected; not in the balance. */
  incoming_cents?: number
}

/** What the automatic top-up read says, of what a banner needs. */
export interface AutoTopUpState {
  enabled: boolean
  consecutive_failures: number
}

/** A top-up, of what deciding whether it waits on the customer's bank needs. */
export interface PurchaseState {
  uid: string
  kind: string
  status: string
  opened_at_ledger: boolean
  expires_at: string
  amount_excl_tax_cents: number
  payment_failure_reason?: string | null
}

export interface BannerInput {
  /** The caller may manage this organization's billing **and** its platform sells credits. */
  managesWallet: boolean
  /** The platform sells credits (`session.sellsCredits()`). */
  sellsCredits: boolean
  /** `GET /access`'s `org.public_side_offline`: the visitor routes' own wallet gate. */
  publicSideOffline: boolean
  /** `GET /access`'s `org.public_side_offline_reason`: why, while it is withheld. */
  publicSideOfflineReason?: PublicSideOfflineReason | null
  /** `GET /access`'s `org.daily_delivery_allowance_bytes`: the day's figure, while one holds it. */
  dailyDeliveryAllowanceBytes?: number | null
  /** The wallet, or `null` while it is not read — or not readable. */
  wallet: WalletState | null | undefined
  autoTopUp: AutoTopUpState | null | undefined
  /** The most recent top-ups, newest first. Only automatic ones are looked at. */
  purchases: readonly PurchaseState[]
  /** The month's delivery against its free allowance (`delivery.ts`), or `null` while unread. */
  delivery?: DeliveryState | null
  /** The prices the delivery past the allowance is estimated at — the catalogue's when omitted. */
  prices?: PriceBook
  /** The session's dismissals. */
  dismissed: ReadonlySet<BalanceBannerKind>
  now?: number
}

/**
 * The automatic top-up that waits on the customer's bank, if one does: opened by the sweep, still
 * payable, and last refused for want of the customer's confirmation — the very case the
 * `billing/auto-top-up-action-required` e-mail links to `?pay=<uid>` for.
 */
export function awaitingConfirmation(
  purchases: readonly PurchaseState[],
  now: number = Date.now(),
): PurchaseState | null {
  return (
    purchases.find(
      (purchase) =>
        purchase.kind === 'credit_auto_top_up' &&
        purchase.status === 'open' &&
        purchase.opened_at_ledger &&
        purchase.payment_failure_reason === AUTHENTICATION_REQUIRED &&
        new Date(purchase.expires_at).getTime() > now,
    ) ?? null
  )
}

export function balanceBanners(input: BannerInput): BalanceBanner[] {
  if (!input.sellsCredits) return []

  if (!input.managesWallet) {
    if (!input.publicSideOffline || input.dismissed.has('member')) return []
    return [{ kind: 'member', reason: input.publicSideOfflineReason ?? 'wallet_empty' }]
  }

  const banners: BalanceBanner[] = []
  const wallet = input.wallet
  const autoTopUp = input.autoTopUp
  const covered = autoTopUp?.enabled === true

  if (wallet) {
    const incomingCents = wallet.incoming_cents ?? 0
    const incoming = incomingCents > 0 ? { incomingCents } : {}
    if (wallet.balance_cents <= 0) {
      banners.push(
        wallet.content_deletable_on
          ? { kind: 'deletion', deletableOn: wallet.content_deletable_on, ...incoming }
          : { kind: 'empty', ...incoming },
      )
    } else if (input.publicSideOffline && input.publicSideOfflineReason === 'daily_allowance') {
      // The day's delivery is spent while credit remains: the wallet's runway is not the news.
      banners.push({ kind: 'dailyAllowance', allowanceBytes: input.dailyDeliveryAllowanceBytes ?? null })
    } else if (!covered && typeof wallet.runway_days === 'number') {
      if (wallet.runway_days <= CRITICAL_RUNWAY_DAYS) {
        banners.push({ kind: 'critical', runwayDays: wallet.runway_days, ...incoming })
      } else if (isRunwayShort(wallet.runway_days) && incomingCents <= 0 && !input.dismissed.has('low')) {
        banners.push({
          kind: 'low',
          runwayDays: wallet.runway_days,
          spentPerDayCents: wallet.spent_per_day_cents,
        })
      }
    }
  }

  if (covered) {
    const waiting = awaitingConfirmation(input.purchases, input.now)
    if (waiting) {
      banners.push({
        kind: 'confirmation',
        purchaseId: waiting.uid,
        amountExclTaxCents: waiting.amount_excl_tax_cents,
      })
    }
  } else if (autoTopUp && autoTopUp.consecutive_failures >= AUTO_TOP_UP_FAILURE_LIMIT) {
    banners.push({ kind: 'autoTopUpOff', failures: autoTopUp.consecutive_failures })
  }

  // Delivery past the allowance offered to the organization: nearing the allowance or passing it
  // costs nothing, so neither is news.
  const deliveryOffered = (input.prices ?? CATALOG_PRICES).prices.bandwidth.offered
  const delivery = deliveryOffered ? null : (input.delivery ?? null)
  const level = deliveryLevel(delivery)
  if (delivery && level === 'exceeded' && !input.dismissed.has('deliveryExceeded')) {
    banners.push({
      kind: 'deliveryExceeded',
      includedBytes: delivery.includedBytes,
      billableBytes: delivery.billableBytes,
      billedCents: deliveryBilledCents(delivery.billableBytes, input.prices),
    })
  } else if (delivery && level === 'near' && !input.dismissed.has('deliveryNear')) {
    banners.push({
      kind: 'deliveryNear',
      usedBytes: delivery.usedBytes,
      includedBytes: delivery.includedBytes,
    })
  }

  return banners
}

/**
 * The wallet's state in one word, for what draws a balance in colour — the rail's chip (U5).
 * The same thresholds as the banners, and the same silence while the automatic top-up covers
 * the wallet: a balance it is about to refill is not a warning.
 */
export type RunwayTone = 'empty' | 'critical' | 'low' | 'fine'

export function runwayTone(wallet: WalletState, autoTopUpOn: boolean): RunwayTone {
  if (wallet.balance_cents <= 0) return 'empty'
  if (autoTopUpOn || typeof wallet.runway_days !== 'number') return 'fine'
  if (wallet.runway_days <= CRITICAL_RUNWAY_DAYS) return 'critical'
  return isRunwayShort(wallet.runway_days) ? 'low' : 'fine'
}
